import { ALERT_TYPES, type AlertParams, type AlertParamsByType, type AlertType, type Notification } from '@vlm/api';
import { isolate, type Formatters } from '@vlm/i18n';
import type { T } from '../i18n';

/**
 * A notification's alert type: the API sends it (apps/backend since its post-phase-1 changes); the
 * Java API only titled it "OVERSPEED Alert", so read it back from the title there.
 */
export function notificationType(n: Pick<Notification, 'alertType' | 'title'>): AlertType | null {
  if (n.alertType && (ALERT_TYPES as readonly string[]).includes(n.alertType)) return n.alertType;
  const type = /^([A-Z ]+) Alert$/.exec(n.title)?.[1].replace(/ /g, '_');
  return type && (ALERT_TYPES as readonly string[]).includes(type) ? (type as AlertType) : null;
}

/**
 * An alert in the reader's language, from the values the API stores with it. null when there are
 * none (the Java API, or a message nobody could read back) — then show the API's English text.
 */
export function alertText(type: AlertType, params: AlertParams | null | undefined, t: T, fmt: Formatters): string | null {
  if (!params) return null;
  const vehicle = isolate(params.vehicleName);
  switch (type) {
    case 'OVERSPEED': {
      const p = params as AlertParamsByType['OVERSPEED'];
      return t('alertText.OVERSPEED', { vehicle, speed: fmt.speed(p.speed), limit: fmt.speed(p.limit) });
    }
    case 'HARSH_DRIVING': {
      const p = params as AlertParamsByType['HARSH_DRIVING'];
      return t('alertText.HARSH_DRIVING', { vehicle, change: isolate(`${fmt.number(p.acceleration, 1)} ${t('alertSettings.kmhPerSecond')}`) });
    }
    case 'GEOFENCE_ENTER':
    case 'GEOFENCE_EXIT': {
      const p = params as AlertParamsByType['GEOFENCE_ENTER'];
      return t(`alertText.${type}`, { vehicle, geofence: isolate(p.geofenceName) });
    }
    case 'IDLE_TIME': {
      const p = params as AlertParamsByType['IDLE_TIME'];
      return t('alertText.IDLE_TIME', { vehicle, duration: fmt.duration(p.idleMinutes), limit: fmt.duration(p.limit) });
    }
  }
}
